import { useState, useEffect, useCallback } from 'react';
import { format, startOfMonth, endOfMonth, subMonths } from 'date-fns';
import { de } from 'date-fns/locale';
import { Download, FileSpreadsheet, Settings, History, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { formatDateTimeBerlin } from '../lib/dateTime';

import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';

export default function DatevExportPage() {
  const { api } = useAuth();
  const [loading, setLoading] = useState(false);
  const [exportRuns, setExportRuns] = useState([]);
  const [settings, setSettings] = useState(null);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  
  // Default to last month
  const lastMonth = subMonths(new Date(), 1);
  const [dateRange, setDateRange] = useState({
    start: format(startOfMonth(lastMonth), 'yyyy-MM-dd'),
    end: format(endOfMonth(lastMonth), 'yyyy-MM-dd'),
  });

  const [settingsForm, setSettingsForm] = useState({
    normal_hours_wage_type: '0001',
    vacation_wage_type: '0010',
    sick_wage_type: '0020',
    special_leave_wage_type: '0030',
    overtime_payout_wage_type: '0040',
    decimal_separator: ',',
    delimiter: ';',
    mileage_rate_per_km: 0.30,
  });

  const fetchData = useCallback(async () => {
    if (!api) return;
    try {
      const [runsRes, settingsRes] = await Promise.all([
        api.get('/datev/export-runs'),
        api.get('/datev/settings'),
      ]);
      setExportRuns(runsRes.data);
      setSettings(settingsRes.data);
      setSettingsForm(settingsRes.data);
    } catch (error) {
      console.error('Error fetching DATEV data:', error);
    }
  }, [api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleExport = async () => {
    try {
      setLoading(true);
      
      // Get auth token from localStorage (same as AuthContext)
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Nicht authentifiziert');
        setLoading(false);
        return;
      }
      
      // Use XMLHttpRequest instead of fetch to avoid body stream issues
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${process.env.REACT_APP_BACKEND_URL}/api/datev/export/payroll?start_date=${dateRange.start}&end_date=${dateRange.end}`, true);
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.setRequestHeader('Content-Type', 'application/json');
      xhr.responseType = 'blob';
      
      xhr.onload = () => {
        setLoading(false);
        
        if (xhr.status >= 400) {
          // Error response - read as text
          const reader = new FileReader();
          reader.onload = () => {
            try {
              const errorData = JSON.parse(reader.result);
              toast.error(errorData.detail || 'Export fehlgeschlagen');
            } catch {
              toast.error(reader.result || 'Export fehlgeschlagen');
            }
          };
          reader.onerror = () => toast.error('Export fehlgeschlagen');
          reader.readAsText(xhr.response);
          return;
        }
        
        // Success - download file
        const blob = new Blob([xhr.response], { type: 'text/csv;charset=utf-8' });
        const filename = `DATEV_Lohn_${dateRange.start}_${dateRange.end}.csv`;
        
        // Try different download methods for browser compatibility
        if (window.navigator && window.navigator.msSaveOrOpenBlob) {
          // IE/Edge
          window.navigator.msSaveOrOpenBlob(blob, filename);
        } else {
          // Modern browsers
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.style.display = 'none';
          link.href = url;
          link.download = filename;
          
          // Append to body and click
          document.body.appendChild(link);
          
          // Use setTimeout to ensure the link is in the DOM
          setTimeout(() => {
            link.click();
            
            // Cleanup after a delay
            setTimeout(() => {
              document.body.removeChild(link);
              window.URL.revokeObjectURL(url);
            }, 100);
          }, 0);
        }
        
        toast.success('DATEV Export erfolgreich');
        fetchData();
      };
      
      xhr.onerror = () => {
        setLoading(false);
        toast.error('Netzwerkfehler beim Export');
      };
      
      xhr.send();
    } catch (error) {
      console.error('DATEV Export error:', error);
      toast.error('Fehler beim Export');
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      await api.put('/datev/settings', settingsForm);
      toast.success('Einstellungen gespeichert');
      setIsSettingsOpen(false);
      setSettings(settingsForm);
    } catch (error) {
      toast.error('Fehler beim Speichern');
    }
  };

  const setPreset = (preset) => {
    const now = new Date();
    let start, end;
    
    switch (preset) {
      case 'this_month':
        start = startOfMonth(now);
        end = endOfMonth(now);
        break;
      case 'last_month':
        start = startOfMonth(subMonths(now, 1));
        end = endOfMonth(subMonths(now, 1));
        break;
      case 'last_quarter':
        const quarterStart = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3 - 3, 1);
        start = quarterStart;
        end = new Date(quarterStart.getFullYear(), quarterStart.getMonth() + 3, 0);
        break;
      default:
        return;
    }
    
    setDateRange({
      start: format(start, 'yyyy-MM-dd'),
      end: format(end, 'yyyy-MM-dd'),
    });
  };

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <FileSpreadsheet className="w-6 h-6" />
            DATEV Export
          </h1>
          <p className="text-muted-foreground mt-1">Lohndaten für DATEV exportieren</p>
        </div>
        <Button
          variant="outline"
          onClick={() => setIsSettingsOpen(true)}
          className="border-border"
        >
          <Settings className="w-4 h-4 mr-2" />
          Einstellungen
        </Button>
      </div>

      <Tabs defaultValue="export" className="space-y-4">
        <TabsList className="bg-card/50 border-border">
          <TabsTrigger value="export">Export</TabsTrigger>
          <TabsTrigger value="history">Verlauf</TabsTrigger>
        </TabsList>

        <TabsContent value="export" className="space-y-4">
          {/* Export Card */}
          <Card className="bg-card/50 border-border">
            <CardHeader>
              <CardTitle className="text-foreground">Lohn-Export</CardTitle>
              <CardDescription className="text-muted-foreground">
                Exportiert Arbeitszeiten, Urlaub, Krankheit und Mehrarbeit-Auszahlungen
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Presets */}
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPreset('this_month')}
                  className="border-border"
                >
                  Dieser Monat
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPreset('last_month')}
                  className="border-border"
                >
                  Letzter Monat
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPreset('last_quarter')}
                  className="border-border"
                >
                  Letztes Quartal
                </Button>
              </div>

              {/* Date Range */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Von</Label>
                  <Input
                    type="date"
                    value={dateRange.start}
                    onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                    className="bg-input border-border text-foreground"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Bis</Label>
                  <Input
                    type="date"
                    value={dateRange.end}
                    onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                    className="bg-input border-border text-foreground"
                  />
                </div>
              </div>

              {/* Info Box */}
              <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
                <div className="flex gap-3">
                  <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-700 dark:text-blue-300">
                    <p className="font-medium mb-1">Hinweis zum Export:</p>
                    <ul className="list-disc list-inside space-y-1 text-blue-700 dark:text-blue-300/80">
                      <li>Nur genehmigte Wochen werden exportiert</li>
                      <li>Alle Mitarbeiter benötigen eine Personalnummer</li>
                      <li>Mehrarbeit-Auszahlungen werden als exportiert markiert</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Export Button */}
              <Button
                onClick={handleExport}
                disabled={loading}
                className="w-full bg-cyan-600 hover:bg-cyan-700"
              >
                {loading ? (
                  'Exportiere...'
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-2" />
                    CSV Export starten
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Current Settings */}
          {settings && (
            <Card className="bg-card/50 border-border">
              <CardHeader>
                <CardTitle className="text-foreground text-sm">Aktuelle Lohnarten</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Normalstunden</p>
                    <p className="text-foreground font-mono">{settings.normal_hours_wage_type}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Urlaub</p>
                    <p className="text-foreground font-mono">{settings.vacation_wage_type}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Krankheit</p>
                    <p className="text-foreground font-mono">{settings.sick_wage_type}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Mehrarbeit</p>
                    <p className="text-foreground font-mono">{settings.overtime_payout_wage_type}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="history">
          <Card className="bg-card/50 border-border">
            <CardHeader>
              <CardTitle className="text-foreground flex items-center gap-2">
                <History className="w-5 h-5" />
                Export-Verlauf
              </CardTitle>
            </CardHeader>
            <CardContent>
              {exportRuns.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">Noch keine Exporte durchgeführt</p>
              ) : (
                <div className="space-y-3">
                  {exportRuns.map((run) => (
                    <div
                      key={run.id}
                      className="flex flex-col sm:flex-row sm:items-center justify-between p-3 bg-muted/50 rounded-lg gap-2"
                    >
                      <div>
                        <p className="text-foreground font-medium">{run.file_name}</p>
                        <p className="text-muted-foreground text-sm">
                          {formatDateTimeBerlin(run.created_at, 'dd.MM.yyyy HH:mm')} • {run.record_count} Einträge
                        </p>
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {run.period_start} bis {run.period_end}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Settings Dialog */}
      <Dialog open={isSettingsOpen} onOpenChange={setIsSettingsOpen}>
        <DialogContent className="bg-card border-border text-foreground max-w-lg">
          <DialogHeader>
            <DialogTitle>DATEV Einstellungen</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-muted-foreground">Normalstunden</Label>
                <Input
                  value={settingsForm.normal_hours_wage_type}
                  onChange={(e) => setSettingsForm({ ...settingsForm, normal_hours_wage_type: e.target.value })}
                  className="bg-input border-border text-foreground font-mono"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-muted-foreground">Urlaub</Label>
                <Input
                  value={settingsForm.vacation_wage_type}
                  onChange={(e) => setSettingsForm({ ...settingsForm, vacation_wage_type: e.target.value })}
                  className="bg-input border-border text-foreground font-mono"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-muted-foreground">Krankheit</Label>
                <Input
                  value={settingsForm.sick_wage_type}
                  onChange={(e) => setSettingsForm({ ...settingsForm, sick_wage_type: e.target.value })}
                  className="bg-input border-border text-foreground font-mono"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-muted-foreground">Sonderurlaub</Label>
                <Input
                  value={settingsForm.special_leave_wage_type}
                  onChange={(e) => setSettingsForm({ ...settingsForm, special_leave_wage_type: e.target.value })}
                  className="bg-input border-border text-foreground font-mono"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-muted-foreground">Mehrarbeit-Auszahlung</Label>
                <Input
                  value={settingsForm.overtime_payout_wage_type}
                  onChange={(e) => setSettingsForm({ ...settingsForm, overtime_payout_wage_type: e.target.value })}
                  className="bg-input border-border text-foreground font-mono"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-muted-foreground">Km-Satz (EUR)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={settingsForm.mileage_rate_per_km}
                  onChange={(e) => setSettingsForm({ ...settingsForm, mileage_rate_per_km: parseFloat(e.target.value) })}
                  className="bg-input border-border text-foreground"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-muted-foreground">Dezimaltrennzeichen</Label>
                <Input
                  value={settingsForm.decimal_separator}
                  onChange={(e) => setSettingsForm({ ...settingsForm, decimal_separator: e.target.value })}
                  className="bg-input border-border text-foreground font-mono"
                  maxLength={1}
                />
              </div>
              <div className="space-y-2">
                <Label className="text-muted-foreground">Feldtrennzeichen</Label>
                <Input
                  value={settingsForm.delimiter}
                  onChange={(e) => setSettingsForm({ ...settingsForm, delimiter: e.target.value })}
                  className="bg-input border-border text-foreground font-mono"
                  maxLength={1}
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => setIsSettingsOpen(false)}>
                Abbrechen
              </Button>
              <Button onClick={handleSaveSettings} className="bg-cyan-600 hover:bg-cyan-700">
                Speichern
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
